import { AnimationBaseController, IAnimationControllerConfiguration, Inject } from '@denishussar/html5-game-engine';
import { filters, Graphics, Sprite, Texture } from 'pixi.js';
import { AnimationContents } from '../../../AnimationContents';
import { GameModel } from '../../Models/GameModel';
import isMobile from 'is-mobile';

export interface IPaletteListItemControllerOptions {
    color: number;
    index: number;
    progress: number;
    symbol: string;
    onClick: (index: number) => void;
    onPointerDown?: (index: number) => void;
}

export class PaletteListItemController extends AnimationBaseController {
    static height: number = 110;
    static width: number = 110;

    $model: GameModel;

    private _text: Sprite;
    private _colorFilter: filters.ColorMatrixFilter;
    private _progress: Sprite;

    private alphabetPaletteTexture: Texture[] = [];

    public constructor(
        @Inject private _options: IPaletteListItemControllerOptions,
    ) {
        super('PaletteListItemController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        let pointDownX = 0;
        let pointDownY = 0;
        const maxDeltaMove = Math.floor(PaletteListItemController.width * .10);
        return <IAnimationControllerConfiguration>{
            animationContent: isMobile() ? AnimationContents.GAME_SCREEN_MOBILE_JSON : AnimationContents.GAME_SCREEN_JSON,
            libraryName: this._options.color > -1 ? 'palette_gag_container' : 'palette_hand',
            onPointerDown: (e) => {
                pointDownX = e.globalPosition.x;
                pointDownY = e.globalPosition.y;
                if (this._options.onPointerDown) {
                    this._options.onPointerDown(this.getIndex());
                }
            },
            onPointerUp: (e) => {
                if (
                    this._options.onClick
                    && Math.abs(pointDownX - e.globalPosition.x) <= maxDeltaMove
                    && Math.abs(pointDownY - e.globalPosition.y) <= maxDeltaMove
                ) {
                    this._options.onClick(this.getIndex());
                }
            },
            modelEvents: {
                progressColor: (color: number, index: number, progress: number) => {
                    if (this._options.color !== color) {
                        return;
                    }
                    this.updateProgress(progress);
                }
            }
        };
    }

    protected onInitialize(): void {
        this.gotoAndStop('inactive');

        if (this._options.color > -1) {
            const tint = this._options.color;
            const r = tint >> 16 & 0xFF;
            const g = tint >> 8 & 0xFF;
            const b = tint & 0xFF;

            const gag = this.getDisplayObjectByQuery('gag')[0];

            // console.log('gag', gag);

            this._colorFilter = new filters.ColorMatrixFilter();
            this._colorFilter.matrix[0] = r / 255;
            this._colorFilter.matrix[6] = g / 255;
            this._colorFilter.matrix[12] = b / 255;

            gag.filters = [this._colorFilter];
            this.display.hitArea = gag.getLocalBounds();

            this.bakeTextures();

            this._text = new Sprite(this.alphabetPaletteTexture[GameModel.alphabetSymbol.indexOf(this._options.symbol)]);
            this._text.anchor.set(0.5);
            this._text.scale.set(1.25);
            this._text.interactive = false;
            this.display.addChild(this._text);

            this._progress = new Sprite();
            this._progress.anchor.set(0.5);
            this._progress.interactive = false;
            this._progress.addChild(new Graphics());
            this.display.addChildAt(this._progress, 0);

            this.updateProgress(this._options.progress);
        }
    }

    private bakeTextures() {
        const fontSize = 48;
        const darkTextLabel = new PIXI.Text('', {
            fontSize: fontSize,
            fill: 0x000000,
            align: 'center',
        });
        darkTextLabel.anchor.set(0.5);

        GameModel.alphabetSymbol
        .forEach(symbol => {
            if (this.$model.palette.length - 1 > this.alphabetPaletteTexture.length) {
                const label = darkTextLabel;
                label.text = symbol.toString();
                label.scale.set(1);
                label.alpha = 1;
                this.alphabetPaletteTexture.push(this.renderer.renderer.generateTexture(label));
            }
        });
    }

    protected onResize(width: number, height: number, scale: number) {
    }

    public select(): void {
        this.gotoAndStop('active');
    }

    public unselect(): void {
        this.gotoAndStop('inactive');
    }

    public getIndex(): number {
        return this._options.index;
    }

    private updateProgress(progress: number): void {
        const g: Graphics = this._progress.getChildAt(0);
        g.clear();

        const mainColor = 0x589a08;
        const sectorColor = 0x70a92c;
        const index = Math.floor(progress * 10);
        const radius = Math.floor(PaletteListItemController.width / 2 * 1.1);
        
        g.beginFill(mainColor);
        g.moveTo(0, 0);
        g.lineTo(0, -radius);
        g.arc(0, 0, radius, -Math.PI / 2, 2 * Math.PI / 10 * index - Math.PI / 2);
        g.endFill();
    }
}
